"use client";

// "On this page" navigation. Sticky beside the article from lg; a horizontally
// scrolling chip row pinned under the site header below that. Highlights the
// section currently in view. Jump links glide via the layout's smooth-scroll rule.

import * as React from "react";

import { cn } from "@/lib/utils";

interface TocItem {
  id: string;
  label: string;
}

interface ArticleTocProps {
  label: string;
  items: TocItem[];
}

export function ArticleToc({ label, items }: ArticleTocProps) {
  const [activeId, setActiveId] = React.useState<string | null>(null);

  React.useEffect(() => {
    const sections = items
      .map((item) => document.getElementById(item.id))
      .filter((element): element is HTMLElement => element !== null);
    if (sections.length === 0) return;

    const visible = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visible.add(entry.target.id);
          else visible.delete(entry.target.id);
        }
        const current = items.find((item) => visible.has(item.id));
        if (current) setActiveId(current.id);
      },
      { rootMargin: "-20% 0px -60% 0px" }
    );
    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [items]);

  return (
    <nav
      aria-label={label}
      data-testid="mca-toc"
      className="bg-grey-0 sticky top-[var(--nav-offset)] z-10 -mx-5 px-5 py-3 md:-mx-10 md:px-10 lg:top-[calc(var(--nav-offset)+24px)] lg:mx-0 lg:self-start lg:px-0 lg:py-0"
    >
      <p className="text-grey-600 mb-3 hidden text-xs lg:block">{label}</p>
      <ul className="flex gap-2 overflow-x-auto lg:flex-col lg:overflow-visible">
        {items.map((item) => {
          const active = item.id === activeId;
          return (
            <li key={item.id} className="shrink-0">
              <a
                href={`#${item.id}`}
                aria-current={active ? "location" : undefined}
                className={cn(
                  "text-ds-body-sm rounded-ds-m border-ds-default block border px-2.5 py-2 whitespace-nowrap transition-colors lg:whitespace-normal",
                  active
                    ? "bg-grey-0 border-grey-300 text-grey-950"
                    : "bg-grey-50 border-grey-100 text-grey-700 hover:text-grey-950"
                )}
              >
                {item.label}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
